import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { services } from "@/data/services";
import { cities } from "@/data/cities";
import { CONTACT_PHONE, CONTACT_PHONE_TEL, CONTACT_EMAIL } from "@/components/Nav";
import { TESTIDS } from "@/constants/testIds";

export const Footer = () => {
  const { t, lang } = useLang();

  return (
    <footer
      data-testid={TESTIDS.footer.root}
      className="relative bg-black text-white border-t border-white/10"
    >
      <div className="px-6 md:px-10 pt-24 pb-10 max-w-[1600px] mx-auto">
        <div className="font-display uppercase leading-[0.82] tracking-tight text-white">
          <div className="text-[16vw] md:text-[13vw] lg:text-[10.5vw] font-black">
            Maze Security
          </div>
          <div className="text-[16vw] md:text-[13vw] lg:text-[10.5vw] font-black text-amber-500 -mt-2">
            Solutions
          </div>
          <div className="mt-2 text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-zinc-500">
            Quebec Security Services
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-10 border-t border-white/10 pt-14">
          {/* Contact block replaces newsletter */}
          <div className="md:col-span-1">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-amber-500 mb-4">
              → {lang === "fr" ? "Contact direct" : "Direct contact"}
            </div>
            <a
              href={`tel:${CONTACT_PHONE_TEL}`}
              className="group flex items-start gap-3 mb-4 hover:text-amber-500 transition-colors"
            >
              <Phone className="w-4 h-4 mt-1 text-amber-500 shrink-0" />
              <span className="font-display uppercase text-xl md:text-2xl tracking-tight leading-none">{CONTACT_PHONE}</span>
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="group flex items-start gap-3 hover:text-amber-500 transition-colors"
            >
              <Mail className="w-4 h-4 mt-1 text-amber-500 shrink-0" />
              <span className="text-base md:text-lg break-all">{CONTACT_EMAIL}</span>
            </a>
            <p className="mt-6 text-sm text-zinc-400 leading-relaxed">
              {lang === "fr"
                ? "Bilingue FR/EN. Réponse en un jour ouvrable. Répartition d'urgence 24/7."
                : "Bilingual FR/EN. One business day reply. 24/7 emergency dispatch."}
            </p>
          </div>

          {/* Services sitemap */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4">
              {t("footer.sitemap_services")}
            </div>
            <ul className="space-y-2">
              {services.slice(0, 12).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">
                    {lang === "fr" ? s.name_fr : s.name_en}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" className="text-sm text-amber-500 hover:underline">
                  {t("common.all_services")} →
                </Link>
              </li>
            </ul>
          </div>

          {/* Cities sitemap */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4">
              {t("footer.sitemap_cities")}
            </div>
            <ul className="space-y-2">
              {cities.slice(0, 14).map((c) => (
                <li key={c.slug}>
                  <Link to={`/cities/${c.slug}`} className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">
                    {c.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/cities" className="text-sm text-amber-500 hover:underline">
                  {t("common.all_cities")} →
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4">
              {t("footer.sitemap_company")}
            </div>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/case-studies" className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">Case Studies</Link></li>
              <li><Link to="/insights" className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">{t("nav.insights")}</Link></li>
              <li><Link to="/seo" className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">Coverage Directory</Link></li>
              <li><Link to="/contact" className="text-sm text-zinc-300 hover:text-amber-500 transition-colors">{t("nav.contact")}</Link></li>
            </ul>

            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-8 mb-3">
              {t("footer.sitemap_legal")}
            </div>
            <ul className="space-y-2 text-sm text-zinc-500">
              <li>BSP License · Quebec, Canada</li>
              <li>CNESST Compliant</li>
              <li>ISO 18788 Aligned</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
          <span>© Maze Security Solutions — Quebec Security Services. All rights reserved.</span>
          <span>{t("common.certifications")}</span>
        </div>
      </div>
    </footer>
  );
};
